import { useMemo, useState } from 'react';
import { FormInstance, Select, Spin, Upload } from 'antd';

import { DescriptionImage } from '@components/DescriptionImage/DescriptionImage';
import { TextArea } from '@components/Input';
import {
  CustomDropdown,
  CustomLabel,
  StyledFormItem,
} from '@components/onboarding/form/steps/styles';
import { getUploadLabel } from '@lib/utils/stringHelper';
import {
  ImageContainer,
  StyledCloseIconV,
  StyledUploadButton,
} from '@views/jobs/components/InvitedBox/styles';

import cancelV from '../../../svg/CancleV.svg';
import { questionValidation } from '../hooks/useJobsForm/useJobsForm.helper';

import { UploadBoxContentAttachment } from './common';

const { Option } = Select;

type Props = {
  form: FormInstance;
  loading: boolean;
  columnId: number;
  question: any;
  uploadedImages: { imgSrc: string; columnId: number }[];
  handleInputChange: HandleJobsFormChange;
  handleFileChange: (
    info,
    columnId: number,
    questionLabel: string,
    isMiscAttachment?: boolean
  ) => void;
  handleRemoveImage: (
    itemToRemove: {
      imgSrc: string;
      columnId: number;
    },
    isMiscAttachment?: boolean
  ) => void;
};

export const CustomQuestion = ({
  form,
  loading,
  columnId,
  question,
  uploadedImages,
  handleInputChange,
  handleFileChange,
  handleRemoveImage,
}: Props): JSX.Element => {
  const {
    label,
    type,
    current_value: currentValue,
    validation_rules: {
      responseType,
      isRequired,
      prompt,
      options,
      description_image,
      minimumUploads,
      maximumUploads,
    },
  } = question;

  const finalUploadedImages = useMemo(() => {
    const defaultValue = currentValue?.value;
    if (type !== 'attachment') return uploadedImages;
    if (!defaultValue) return uploadedImages;
    return defaultValue?.map((url) => ({ columnId, imgSrc: url }));
  }, [type, uploadedImages, columnId, currentValue]);
  const uploadedImageCount = uploadedImages.filter(
    (img) => img.columnId === columnId
  )?.length;
  const uploadDisabled = maximumUploads && uploadedImageCount >= maximumUploads;

  // currentUploadCount for attachment validation
  const [currentUploadCount, setCurrentUploadCount] = useState(0);

  return (
    <>
      <CustomLabel
        label={prompt + getUploadLabel(minimumUploads, maximumUploads)}
        requiredSymbol={isRequired}
      />
      <StyledFormItem
        name={label}
        rules={
          isRequired
            ? [
                {
                  required: true,
                  message: 'This field is required',
                  validator: (_, value) =>
                    questionValidation(
                      type,
                      value,
                      currentUploadCount,
                      minimumUploads,
                      maximumUploads
                    ),
                },
              ]
            : []
        }
      >
        {responseType === 'Free text' ? (
          <>
            {description_image ? (
              <DescriptionImage description_image={description_image} />
            ) : null}
            <TextArea
              autofit
              placeholder='Type here'
              onChange={(e) =>
                handleInputChange(form, columnId, e.target.value, label, true)
              }
              onBlur={() => isRequired && form.validateFields([label])}
              defaultValue={currentValue?.value}
            />
          </>
        ) : type === 'attachment' ? (
          <>
            {description_image ? (
              <DescriptionImage description_image={description_image} />
            ) : null}
            {currentValue?.value !== null ? (
              <ImageContainer loading={(loading || false).toString()}>
                {loading ? (
                  <Spin />
                ) : (
                  finalUploadedImages
                    .filter((img) => img.columnId === columnId) // Filter by customListId
                    .map((item, index) => (
                      <div key={index}>
                        <StyledCloseIconV
                          src={cancelV.src}
                          onClick={() => {
                            handleRemoveImage(item, true);
                            setCurrentUploadCount((prevCount) => prevCount - 1);
                            isRequired && form.validateFields([label]);
                          }}
                        />
                        <img
                          style={{ width: '80px' }}
                          src={item.imgSrc}
                          alt={`Image ${index}`}
                        />
                      </div>
                    ))
                )}
              </ImageContainer>
            ) : (
              <ImageContainer>
                {currentValue?.value.map((url, index) => (
                  <div key={index}>
                    <StyledCloseIconV src={cancelV.src} />
                    <img
                      style={{
                        width: '80px',
                      }}
                      src={url}
                      alt={`Image ${index}`}
                    />
                  </div>
                ))}
              </ImageContainer>
            )}
            {currentValue?.value === undefined && (
              <Upload
                showUploadList={false}
                accept='.png,.jpeg,.jpg'
                multiple={true}
                beforeUpload={() => {
                  return false; // Prevent default upload behavior
                }}
                onChange={(e) => {
                  handleFileChange(e, columnId, label, true);
                  setCurrentUploadCount(e?.fileList?.length);
                  isRequired && form.validateFields([label]);
                }}
                disabled={uploadDisabled}
              >
                <StyledUploadButton disabled={uploadDisabled}>
                  <UploadBoxContentAttachment />
                </StyledUploadButton>
              </Upload>
            )}
          </>
        ) : responseType === 'Multiple-choice' ? (
          <>
            {description_image ? (
              <DescriptionImage description_image={description_image} />
            ) : null}
            <CustomDropdown
              mode='multiple'
              placeholder='Select all that apply'
              onChange={(value) =>
                handleInputChange(form, columnId, value, label, true)
              }
              onBlur={() => isRequired && form.validateFields([label])}
              defaultValue={currentValue?.value}
            >
              {options?.map((item) => (
                <Option key={item} value={item}>
                  {item}
                </Option>
              ))}
            </CustomDropdown>
          </>
        ) : (
          <>
            {/* Single-choice */}
            {description_image ? (
              <DescriptionImage description_image={description_image} />
            ) : null}
            <CustomDropdown
              placeholder='Select One'
              onChange={(value) =>
                handleInputChange(form, columnId, value, label, true)
              }
              onBlur={() => isRequired && form.validateFields([label])}
              defaultValue={currentValue?.value}
            >
              {options?.map((item) => (
                <Option key={item} value={item}>
                  {item}
                </Option>
              ))}
            </CustomDropdown>
          </>
        )}
      </StyledFormItem>
    </>
  );
};
